import backend, {COMMUNICATION_API, COMMUNICATION_WS_API} from 'utils/backend/server/api_library';
import {getJwtToken} from 'utils/auth/getSession';
import queryString from 'query-string';

export const CHANNEL_CONNECTION_STATUS = {
  CONNECTED: 'CONNECTED',
  DISCONNECTED: 'DISCONNECTED',
};

class ChannelWebsocketManager {
  constructor({onMessage, onMessages, onStatusChange, onReconnect} = {}) {
    this.ws = null;
    this.connected = null;
    this.onMessage = onMessage;
    this.onMessages = onMessages;
    this.onStatusChange = onStatusChange;
    this.onReconnect = onReconnect;
    this.status = null;
    this.conversationRn = null;
    this.hasConnected = false;
    this.hasClosed = false;
  }

  setStatus(nextStatus) {
    if (this.status === nextStatus) return;

    this.status = nextStatus;
    this.onStatusChange?.(nextStatus);
  }

  async connect(conversationRn) {
    if (!conversationRn) return;

    this.hasClosed = false;
    this.conversationRn = conversationRn;
    this.ws?.close();

    let socket;

    try {
      const token = await getJwtToken();
      const url = `${COMMUNICATION_WS_API}?token=${encodeURIComponent(token)}&conversationRn=${encodeURIComponent(conversationRn)}`;
      socket = new WebSocket(url);
    } catch (error) {
      console.error('Failed to open the channel websocket:', error);
      this.connected = Promise.resolve();
      this.setStatus(CHANNEL_CONNECTION_STATUS.DISCONNECTED);
      return;
    }

    if (this.hasClosed) {
      socket.close();
      return;
    }

    this.ws = socket;

    this.connected = new Promise((resolve) => {
      socket.onopen = () => {
        if (this.ws !== socket) return;

        const wasReconnect = this.hasConnected;
        this.hasConnected = true;
        this.setStatus(CHANNEL_CONNECTION_STATUS.CONNECTED);
        resolve();

        if (wasReconnect) {
          this.onReconnect?.();
        }
      };

      socket.onclose = () => {
        resolve();

        if (this.ws !== socket || this.hasClosed) return;

        this.ws = null;
        this.setStatus(CHANNEL_CONNECTION_STATUS.DISCONNECTED);
      };
    });

    socket.onmessage = (evt) => {
      let data;
      try {
        data = JSON.parse(evt.data);
      } catch {
        return;
      }
      if (data?.message) {
        this.onMessage?.(data.message);
      } else if (data?.messages?.items) {
        this.onMessages?.(data.messages.items);
      }
    };

    socket.onerror = (event) => {
      console.error('Channel websocket error:', event);
    };
  }

  reconnect() {
    if (!this.conversationRn) return;

    this.connect(this.conversationRn);
  }

  async send({conversationRn, requesterRn, message, files}) {
    await this.connected;

    if (this.ws?.readyState !== WebSocket.OPEN) {
      throw new Error('The channel websocket is not connected.');
    }

    this.ws.send(JSON.stringify({action: 'sendMessage', conversationRn, requesterRn, message, files}));
  }

  close() {
    this.hasClosed = true;
    this.ws?.close();
    this.ws = null;
    this.connected = null;
    this.setStatus(CHANNEL_CONNECTION_STATUS.DISCONNECTED);
  }
}

const createSmsSubscription = (userRn, phoneNumber) => {
  const url = `${COMMUNICATION_API}/user/${userRn}/sms/subscription`;
  const body = {
    phoneNumber,
  };
  return backend.post(url, body);
};

const getSmsSubscription = (userRn) => {
  const url = `${COMMUNICATION_API}/user/${userRn}/sms/subscription`;
  return backend.get(url);
};


const updateSmsSubscription = (userRn, phoneNumber, newSubscriptionStatus) => {
  const url = `${COMMUNICATION_API}/user/${userRn}/sms/subscription`;
  const body = {
    phoneNumber,
    subscribed: newSubscriptionStatus,
  };
  return backend.put(url, body);
};

const getConversationList = (caregiverRn) => {
  return backend.get(`${COMMUNICATION_API}/user/${caregiverRn}/conversation/conversations`);
};

const getSupportConversation = (userRn) => {
  return backend.get(`${COMMUNICATION_API}/user/${userRn}/conversation/support`);
};

const getConversationMessages = (caregiverRn, conversationRn, lastEvaluatedKey) => {
  const queryParam = {
    lastEvaluatedKey,
  };

  const query = queryString.stringify(queryParam, {
    skipNull: true,
    skipEmptyString: true,
  });

  return backend.get(`${COMMUNICATION_API}/user/${caregiverRn}/conversation/${conversationRn}/messages?${query}`);
};

const createConversationMessage = (userRn, conversationRn, body) => {
  return backend.post(`${COMMUNICATION_API}/user/${userRn}/conversation/${conversationRn}/messages`, body);
};

const listChannelsByType = (channelType, lastEvaluatedKey) => {
  const queryParam = {
    channelType,
    lastEvaluatedKey,
  };

  const query = queryString.stringify(queryParam, {
    skipNull: true,
    skipEmptyString: true,
  });

  return backend.get(`${COMMUNICATION_API}/channel?${query}`);
};

const createChannel = (ownerRn, channelType, contextRn) => {
  const body = {
    channelType,
  };

  if (contextRn) {
    body.contextRn = contextRn;
  }

  return backend.post(`${COMMUNICATION_API}/user/${ownerRn}/channel`, body);
};

const listChannelsByUser = (ownerRn, channelType) => {
  const queryParam = {
    channelType,
  };

  const query = queryString.stringify(queryParam, {
    skipNull: true,
    skipEmptyString: true,
  });

  return backend.get(`${COMMUNICATION_API}/user/${ownerRn}/channel?${query}`);
};

const listChannelMessages = (ownerRn, conversationRn, lastEvaluatedKey) => {
  const queryParam = {
    lastEvaluatedKey,
  };

  const query = queryString.stringify(queryParam, {
    skipNull: true,
    skipEmptyString: true,
  });

  return backend.get(`${COMMUNICATION_API}/user/${ownerRn}/channel/${conversationRn}/messages?${query}`);
};

const createChannelMessage = (ownerRn, conversationRn, body) => {
  return backend.post(`${COMMUNICATION_API}/user/${ownerRn}/channel/${conversationRn}/messages`, body);
};

export default {
  createSmsSubscription,
  getSmsSubscription,
  updateSmsSubscription,
  getConversationList,
  getSupportConversation,
  getConversationMessages,
  createConversationMessage,
  createChannel,
  listChannelsByType,
  listChannelsByUser,
  listChannelMessages,
  createChannelMessage,
  ChannelWebsocketManager,
  CHANNEL_CONNECTION_STATUS,
};
